import React, { useState, useEffect, useCallback } from "react"
import { useTheme } from "@material-ui/styles"
import { Box, useMediaQuery } from "@material-ui/core"
import { useHistory, useParams } from "react-router-dom"
import InfiniteScroll from "react-infinite-scroll-component"
import { useApolloClient } from "@apollo/client"
import { queryOrganisationTemplate } from "../../data/jobs/useQueryOrganisationTemplates"
import {
  ProcessTemplateActionCard,
  LoadingSpinner,
  NoItemsMessage,
  SearchInput,
  ScrollToTop,
  IconMessage,
} from "../../components"
import { TemplateViewer } from "../../components/JobViewer/TemplateViewer"
import { useQueryTemplateCategories, useLazyQueryLibraryTemplates, useMutationCopyProcess } from "../../data"

const Library = () => {
  const theme = useTheme()
  const client = useApolloClient()
  const isInline = useMediaQuery(theme.breakpoints.down("xs"))
  const { set, id, action, category } = useParams()
  const history = useHistory()

  const [data, setData] = useState([])
  const [total, setTotal] = useState(null)
  const [hasMore, setHasMore] = useState(false)
  const [dataLoading, setDataLoading] = useState(false)
  const [searchText, setSearchText] = useState(null)
  const [editing, setEditing] = useState(null)
  const [viewing, setViewing] = useState(null)
  const { data: templateCategoriesData } = useQueryTemplateCategories()
  const [loadLibraryTemplates, { data: libraryData, loading: libraryLoading, loadMore: libraryLoadMore }] =
    useLazyQueryLibraryTemplates()
  const [copyProcess] = useMutationCopyProcess()

  useEffect(() => {
    if (category) {
      setDataLoading(true)
      loadLibraryTemplates({ variables: { category, searchText } })
    }
  }, [category, loadLibraryTemplates, searchText])

  useEffect(() => {
    if (category && libraryData) {
      setData([...libraryData.libraryTemplates.items])
      setTotal(libraryData.libraryTemplates.total)
      setHasMore(libraryData.libraryTemplates.items.length < libraryData.libraryTemplates.total)
    } else {
      setHasMore(false)
      setTotal(null)
      setData([])
    }
    setDataLoading(false)
  }, [category, libraryData])

  const updateViewEditStates = useCallback(
    async (targetAction, subject, setSubject, diversion, setDiversion) => {
      if (data.length && !libraryLoading) {
        if (action === targetAction && id && (!subject || subject.id !== id)) {
          if (diversion) setDiversion(null)
          const item = data.find((template) => template.id === id)
          if (!item) {
            const updateLoadTemplate = async () => {
              const result = await queryOrganisationTemplate(id, client)
              if (result && result.data) setSubject(result.data.organisationTemplate)
            }
            updateLoadTemplate()
          } else setSubject(item)
        } else if (!id && subject) setSubject(null)
      }
    },
    [action, client, data, id, libraryLoading]
  )

  useEffect(() => {
    updateViewEditStates("view", viewing, setViewing, editing, setEditing)
  }, [editing, updateViewEditStates, viewing])

  const handleCopyProcess = async ({ id: copyId }) => {
    const item = await copyProcess({ variables: { id: copyId } })
    const inactiveSet = set.startsWith("active") ? `in${set}` : set
    if (item.data) history.push(`/templates/${inactiveSet}/${item.data.copyProcess.id}/edit`)
  }

  const handleUsed = async (item) => {
    const inactiveSet = !set ? "inactive-oneoff" : set.startsWith("active") ? `in${set}` : set
    history.push(`/templates/${inactiveSet}/${item.id}/edit`)
  }

  const handleClose = async () => {
    history.push(`/templates/library/${category}`)
  }

  const handleChangeSearchText = (text) => {
    setSearchText(text || null)
  }

  const handleNavigateCategory = (navigateCategory) => {
    history.push(`/templates/library/${navigateCategory}`)
  }

  let heading = "Category"
  if (category && templateCategoriesData) {
    const templateCategory = templateCategoriesData.templateCategories.find((item) => item.id === category)
    if (templateCategory) heading = templateCategory.name
  }

  const openNew = id === "new" && action

  const scrollToTop = isInline && (openNew || !!viewing || !!editing)

  const loading = libraryLoading || dataLoading

  return (
    <>
      {scrollToTop && <ScrollToTop />}

      <TemplateViewer
        template={viewing}
        onCategoryClick={handleNavigateCategory}
        onUsed={handleUsed}
        onClose={handleClose}
      />

      {(!isInline || (isInline && !viewing && !editing && !openNew)) && (
        <>
          <SearchInput
            placeholder={`Search ${heading.toLowerCase()}`}
            debounce={300}
            onChange={handleChangeSearchText}
            loading={loading}
            boxProps={{ pl: 0, pr: 0, mb: isInline ? 2 : 5 }}
          />

          {loading && (!data.length || !libraryData) && (
            <Box display="flex" justifyContent="center">
              <LoadingSpinner size={60} />
            </Box>
          )}
          {data.length > 0 && (
            <InfiniteScroll
              dataLength={data.length}
              next={libraryLoadMore}
              hasMore={hasMore}
              loader={
                <Box display="flex" justifyContent="center">
                  <LoadingSpinner size={60} />
                </Box>
              }
              endMessage={
                !loading &&
                !!total && (
                  <NoItemsMessage>
                    {data.length || "No"} matching item{data.length > 1 ? "s" : ""}
                  </NoItemsMessage>
                )
              }
              style={{ overflow: "visible" }}
            >
              <Box data-cy="Box-templates">
                {data.map((template) => (
                  <Box key={template.id}>
                    <ProcessTemplateActionCard
                      process={template}
                      set={set || "library"}
                      category={category}
                      onCopy={handleCopyProcess}
                      onCategoryClick={handleNavigateCategory}
                    />
                  </Box>
                ))}
              </Box>
            </InfiniteScroll>
          )}
          {!loading && !data.length && <IconMessage name="empty-templates" text="No templates" />}
        </>
      )}
    </>
  )
}

export default Library
